import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { FolderTree, Loader2, Sparkles, X } from "lucide-react";
import { proposePlanFromScope, type CleanupPlan } from "@/lib/api";
import { ContentPicker } from "./ContentPicker";

type Props = {
  open: boolean;
  onClose: () => void;
};

/**
 * Modal that drives {@code POST /plans/from-scope} with persist=true
 * — the UI counterpart to the dev-facing curl example shown on the
 * empty Plans page. Operator picks a Cognos folder (or "all"), an
 * optional tenant id, and submits; on success we navigate straight to
 * the new plan's detail page so the work flows directly into review.
 *
 * <p>The folder picker is the same {@link ContentPicker} the upgrade-
 * readiness page uses, so SOAP browsing (when CLOUDCRAFT_COGNOS_SDK_ENABLED)
 * or the SQL fallback both work transparently.
 */
export function NewPlanDialog({ open, onClose }: Props) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [scope, setScope] = useState("*");
  const [tenantId, setTenantId] = useState("_default");
  const [pickerOpen, setPickerOpen] = useState(false);

  const create = useMutation<CleanupPlan, Error>({
    mutationFn: () => proposePlanFromScope(scope, tenantId, true),
    onSuccess: (plan) => {
      qc.invalidateQueries({ queryKey: ["curate-plans"] });
      onClose();
      navigate(`/plans/${plan.id}`);
    },
  });

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-30 flex items-center justify-center bg-curate-ink/30 p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-plan-heading"
      >
        <div className="w-full max-w-lg rounded-xl2 border border-curate-slate/20 bg-white p-5 shadow-elev">
          <header className="mb-3 flex items-center justify-between">
            <h2
              id="new-plan-heading"
              className="font-display text-base font-semibold text-curate-ink"
            >
              New cleanup plan
            </h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="rounded-md p-1 text-curate-slate hover:bg-curate-mist"
            >
              <X size={14} />
            </button>
          </header>

          <p className="text-2xs text-curate-slate">
            Curate reads the Cognos audit DB for the chosen scope, scores every
            asset, and proposes per-item actions. Nothing is irreversible until
            you approve and the cooling-off window passes.
          </p>

          <div className="mt-4 space-y-3">
            <Field label="Folder scope">
              <div className="flex items-center gap-2">
                <input
                  value={scope}
                  onChange={(e) => setScope(e.target.value)}
                  placeholder="* for everything, or a Cognos search-path"
                  className="w-full rounded-md border border-curate-slate/30 bg-white px-2 py-1 text-sm focus:border-curate-primary focus:outline-none focus:ring-2 focus:ring-curate-primary/20"
                />
                <button
                  type="button"
                  onClick={() => setPickerOpen(true)}
                  className="inline-flex shrink-0 items-center gap-1 rounded-md border border-curate-slate/30 bg-white px-2 py-1 text-2xs font-medium text-curate-ink hover:border-curate-primaryDark"
                  title="Browse Cognos folders"
                >
                  <FolderTree size={11} />
                  Browse…
                </button>
              </div>
            </Field>

            <Field label="Tenant">
              <input
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
                placeholder="_default"
                className="w-full rounded-md border border-curate-slate/30 bg-white px-2 py-1 text-sm font-mono focus:border-curate-primary focus:outline-none focus:ring-2 focus:ring-curate-primary/20"
              />
            </Field>
          </div>

          {create.error && (
            <p
              className="mt-3 rounded-md border border-curate-danger/30 bg-curate-danger/5 p-2 text-2xs text-curate-danger"
              role="alert"
            >
              {failureHint(create.error.message)}
            </p>
          )}

          <footer className="mt-5 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={create.isPending}
              className="rounded-md border border-curate-slate/30 bg-white px-3 py-1.5 text-xs font-medium text-curate-ink hover:bg-curate-mist disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => create.mutate()}
              disabled={create.isPending || !scope.trim() || !tenantId.trim()}
              className="inline-flex items-center gap-1.5 rounded-md bg-curate-primary px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-curate-primaryDark disabled:cursor-not-allowed disabled:opacity-40"
            >
              {create.isPending
                ? <Loader2 size={12} className="animate-spin" />
                : <Sparkles size={12} />}
              Generate plan
            </button>
          </footer>
        </div>
      </div>

      <ContentPicker
        open={pickerOpen}
        title="Pick a Cognos folder for the cleanup plan"
        initialPath={scope.startsWith("/") ? scope : undefined}
        onCancel={() => setPickerOpen(false)}
        onConfirm={(picked) => {
          setScope(picked.path);
          setPickerOpen(false);
        }}
      />
    </>
  );
}

/**
 * Convert a raw axios error message into a useful operator-facing
 * hint. The most common failure mode is the audit DB not being
 * configured (loadFolder returns 503 from the controller); surface
 * that distinctly from generic 5xx so the operator knows where to
 * look.
 */
function failureHint(message: string): string {
  if (message.includes("503") || message.toLowerCase().includes("audit db")) {
    return "Cognos audit DB not configured for this deployment. " +
      "Set CLOUDCRAFT_COGNOS_JDBC_URL in your .env, or enable the SOAP SDK " +
      "(CLOUDCRAFT_COGNOS_SDK_ENABLED=true) so the loader can fall back to live SOAP.";
  }
  return message;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-xs">
      <span className="text-2xs font-medium uppercase tracking-wide text-curate-slate">
        {label}
      </span>
      <div className="mt-0.5">{children}</div>
    </label>
  );
}
